import { Product } from '@virginexperiencedays/products';
import { getLocationInfo, getPriceInfo } from '@virginexperiencedays/header-2024/src/';
import { getProductCategoriesWithLocationTextOverride } from '@virginexperiencedays/components-v2/src/utils/productLocation';

/**
 * Parses the current "Recently Viewed" product array, adds in a new product,
 * and re-serializes for storing into local storage and cookies.
 */
export function updateVpPDP(product: Product, dateTime: number, viewed?: string) {
  // parse current 'viewed' into an object
  const viewedParsed = viewed ? JSON.parse(viewed) : undefined;
  const viewedDict = viewedParsed?.length ? Object.fromEntries(viewedParsed) : undefined;

  // add product into the object
  const added = addProduct(product, dateTime, viewedDict);

  const productsEntries = Object.entries(added);

  const limitedProductsEntries = [...productsEntries]
    // make sure to keep the most recently added ones: DESCENDING
    // NOTE: we spread productsEntries because Array#sort is mutative
    .sort((a, b) => b[1].added - a[1].added)
    // limit to 20
    .slice(0, 20);

  // rebuild arrays from the object:
  // local storage contains full information
  const localStorageProducts: [string, ViewedProductItem][] = limitedProductsEntries;
  // cookie contains only id and added timestamp
  const cookieProductIds: ViewedProductMeta[] = localStorageProducts.map((e) => ({
    id: e[0],
    added: e[1].added,
  }));

  // stringify for easy setting
  return {
    localStorageProducts: JSON.stringify(localStorageProducts),
    cookieProductIds: JSON.stringify(cookieProductIds),
  };
}

function addProduct(
  product: Product,
  dateTime: number,
  vpMap?: { [key: string]: ViewedProductItem }
): { [key: string]: ViewedProductItem } {
  // Do not add if product is not active
  if (product.status !== 'active') return vpMap || {};

  const id: string = product.id.toString();

  if (!vpMap) {
    vpMap = {};
    const item: ViewedProductItem = createStorageItem(product, dateTime);
    vpMap[id] = item;
  }

  if (id in vpMap) {
    vpMap[id].added = dateTime;
    vpMap[id].lastValidated = dateTime;
  } else {
    const item: ViewedProductItem = createStorageItem(product, dateTime);
    vpMap[id] = item;
  }

  return vpMap;
}

const createStorageItem = (product: Product, dateTime: number) => {
  const cardRoute = product.isCollectionProduct
    ? `/collection/${product.slug}`
    : `/product/${product.slug}`;

  const { currentPrice, pastPrice, badge } = getPriceInfo(product);

  const locations = getLocationInfo(product);

  // if product has no location, retrieve only the special categories with custom wording to avoid overloading the data
  const categories = !locations
    ? getProductCategoriesWithLocationTextOverride(product.categories)
    : [];

  return {
    vpCard: {
      id: product.id,
      cardTitle: product.title,
      cardRoute,
      pastPrice,
      currentPrice,
      // used by variation A (old product card)
      cardImage: product.media.mainImage.url,
      // used by variation B (product card 2024)
      cardImages: [product.media.mainImage, ...(product?.media?.images || [])].map((img) => ({
        src: img?.url || '',
        alt: img?.altText || product.title,
      })),
      cardFullSku: product.promocode ? `${product.sku} ${product.promocode}` : product.sku,
      categories, // categories for location text override for product card 2024
      percentOff: product.price?.percentOff,
      reviews: product.reviews,
      locations,
      badge,
      dateActivated: product.dateActivated,
      isExclusive: product.isExclusive,
      productPromo: product.productPromo,
    } as ViewedProductCard,
    added: dateTime,
    lastValidated: dateTime,
  } as ViewedProductItem;
};

export type ViewedProductCard = {
  id: number;
  cardRoute: string;
  cardImage: string;
  cardImages?: { src: string; alt: string }[];
  cardFullSku?: string;
  cardTitle: string;
  currentPrice: number;
  pastPrice?: number;
  percentOff?: number;
  locations: number | string;
  reviews: { averageRating: number; count: number };
  badge?: string;
  dateActivated: number;
  isExclusive: boolean;
  categories?: string[];
};

export type ViewedProductItem = {
  vpCard: ViewedProductCard;
  added: number;
  lastValidated: number;
};

type ViewedProductMeta = {
  id: string;
  added: number;
};
